import { createFileRoute, useRouter } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Eye, EyeOff } from "lucide-react";
import { useAuth } from "@/project/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";

export const Route = createFileRoute("/auth")({
  head: () => ({
    meta: [
      { title: "Staff Sign In · Assistance & Letter Desk" },
      {
        name: "description",
        content:
          "Secure internal sign in for assistance desk staff and administrators.",
      },
      {
        property: "og:title",
        content: "Staff Sign In · Assistance & Letter Desk",
      },
      {
        property: "og:description",
        content: "Secure internal sign in for assistance desk staff.",
      },
    ],
  }),
  component: AuthPage,
});

function AuthPage() {
  const router = useRouter();
  const { user, loading, login } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!loading && user) {
      router.navigate({ to: "/" });
    }
  }, [user, loading, router]);

  const signIn = async () => {
    if (!email || !password) {
      toast.error("Please enter email and password to continue");
      return;
    }
    setBusy(true);
    try {
      await login(email, password);
      toast.success("Successfully signed in!");
      router.navigate({ to: "/" });
    } catch (e) {
      let message = "Could not sign in";
      if (e instanceof Error) {
        const axiosError = e as any;
        message = axiosError.response?.data?.error?.message ??
                  axiosError.response?.data?.message ??
                  axiosError.response?.data?.error ??
                  e.message;
      }
      toast.error(message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-sidebar px-4 py-10">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <CardTitle className="font-display text-xl">
            Sheth Shree Motisha Sadharmik Bhakti Kendra
          </CardTitle>
          <CardDescription>
            Financial Assistance &amp; Recommendation Letter System — internal
            staff access only
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="email">Email</Label>
            <Input
              id="email"
              type="email"
              placeholder="email@example.com"
              value={email}
              onChange={e => setEmail(e.target.value)}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="pwd">Password</Label>
            <div className="relative">
              <Input
                id="pwd"
                type={showPassword ? "text" : "password"}
                placeholder="••••••••"
                className="pr-10"
                value={password}
                onChange={e => setPassword(e.target.value)}
                onKeyDown={e => e.key === "Enter" && signIn()}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground focus:outline-none"
              >
                {showPassword ? (
                  <EyeOff className="h-4 w-4" />
                ) : (
                  <Eye className="h-4 w-4" />
                )}
              </button>
            </div>
          </div>
          <Button className="w-full" disabled={busy} onClick={signIn}>
            {busy ? "Signing in…" : "Sign In"}
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}
